import { API_CONFIG } from '@/config/api';

/**
 * Path on the workspace origin that the popup lands on once a code is minted.
 *
 * Must stay identical to HANDOFF_COMPLETE_PATH in
 * control-plane-ui/src/features/workspaces/pages/WorkspaceHandoffPage.tsx. The
 * two live in separate packages and cannot share a module; a silent divergence
 * would land the popup on a route the workspace does not serve, and the flow
 * would fail with no error.
 */
export const HANDOFF_COMPLETE_PATH = '/auth/handoff-complete';

/** Discriminator for the one message this flow sends. */
export const HANDOFF_MESSAGE_TYPE = 'e6-handoff';

export interface HandoffMessage {
  type: typeof HANDOFF_MESSAGE_TYPE;
  status: 'ok' | 'needs-login';
  nonce: string;
}

export type ReauthResult =
  /** A workspace session now exists. Retry whatever failed; do not navigate. */
  | 'ok'
  /** The control-plane session is gone too. Fall back to a full redirect. */
  | 'needs-login'
  /** We could not find out. Treat as needs-login; the fallback is the same. */
  | 'unavailable';

const POPUP_WIDTH = 420;
const POPUP_HEIGHT = 480;

/**
 * Window features for the handoff popup.
 *
 * A window is unavoidable here: the control plane's session cookie is
 * SameSite=Lax, so it travels only on a top-level navigation — an iframe would
 * be sent nothing. Given that, the aim is for it to read as a deliberate
 * sign-in step rather than a stray window, so it is small and centred on the
 * current window instead of landing at the screen's top-left corner.
 *
 * `noopener` is deliberately absent: the popup must keep `window.opener` to
 * report back, and this tab needs the returned handle to watch `popup.closed`.
 * Omitting the feature is already the default — the previous `noopener=no`
 * said exactly the same thing while reading as though it did something.
 */
function popupFeatures(): string {
  const left = Math.round(Math.max(0, window.screenX + (window.outerWidth - POPUP_WIDTH) / 2));
  // A third of the way down reads better than dead-centre for a short-lived window.
  const top = Math.round(Math.max(0, window.screenY + (window.outerHeight - POPUP_HEIGHT) / 3));
  return `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`;
}
const CLOSE_POLL_MS = 400;
const TIMEOUT_MS = 60_000;

/**
 * Builds the control-plane entry URL for a handoff.
 *
 * Returns null when the workspace id is unknown — a workspace whose first poll
 * has not completed. Callers degrade to the plain control-plane URL rather than
 * sending a request the control plane cannot act on.
 */
export function buildHandoffEntryUrl(mode: 'popup' | 'redirect', nonce?: string): string | null {
  const { controlPlaneUrl, workspaceId } = API_CONFIG;
  if (!controlPlaneUrl || !workspaceId) return null;

  // `new URL(path, base)` still does the trailing-slash-safe join of
  // controlPlaneUrl and the route; only origin/pathname are read off it.
  // Params are assembled on a standalone URLSearchParams and joined manually
  // rather than via `url.searchParams.set(...)` + `url.toString()`: this
  // repo's jest.setup.js replaces global URL/URLSearchParams with a jsdom
  // shim whose URL#searchParams.set() never syncs back into url.href. Both
  // forms are equivalent against a real URL implementation.
  const url = new URL('/workspace-handoff', controlPlaneUrl);
  const params = new URLSearchParams();
  params.set('ws', workspaceId);
  params.set('mode', mode);
  if (mode === 'popup' && nonce) {
    params.set('nonce', nonce);
  }
  if (mode === 'redirect') {
    params.set('path', `${window.location.pathname}${window.location.search}`);
  }
  return `${url.origin}${url.pathname}?${params.toString()}`;
}

/**
 * Mints the nonce that ties one popup's report back to the call that opened it.
 *
 * `crypto.randomUUID` exists only in a secure context, which local and test
 * deployments served over plain HTTP genuinely are not — hence the
 * `getRandomValues` fallback, which carries no such restriction and is equally
 * unpredictable.
 *
 * There is deliberately no `Math.random` path. The nonce is not itself a
 * credential — origin and source are the checks an attacker cannot forge — but
 * a predictable value weakens the third check for nothing, and returning null
 * here costs only a fall back to the full redirect, which lands the user in the
 * same place. Failing closed is cheaper than a weak nonce.
 *
 * @returns a nonce, or null when no cryptographic source is available
 */
function generateNonce(): string | null {
  if (typeof crypto === 'undefined') return null;

  // Widened deliberately: the DOM lib declares both members as always present,
  // so an `in` check narrows the remaining branch to `never` and will not
  // compile. Outside a secure context randomUUID really is missing at runtime,
  // so the guard has to test the value rather than the declared type.
  const source = crypto as Partial<Crypto>;

  if (typeof source.randomUUID === 'function') return source.randomUUID();
  if (typeof source.getRandomValues === 'function') {
    const bytes = new Uint8Array(16);
    source.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  }
  return null;
}

/**
 * Restores the workspace session without leaving the page, when possible.
 *
 * A popup is a top-level navigation, so the control plane's SameSite=Lax session
 * cookie travels with it — no CORS and no cookie-policy change are needed. The
 * popup's final hop is to THIS origin, so the cookie compute-api sets there is
 * immediately live in this tab; that is what makes the restore silent.
 *
 * Resolution is whichever comes first: our popup reporting success, the popup
 * closing without reporting (the control plane had no session either), or a
 * timeout. Every path closes the popup and detaches its listeners.
 */
export async function attemptSilentReauth(): Promise<ReauthResult> {
  const nonce = generateNonce();
  if (!nonce) return 'unavailable';

  const entryUrl = buildHandoffEntryUrl('popup', nonce);
  if (!entryUrl) return 'unavailable';

  const popup = window.open(entryUrl, 'e6-handoff', popupFeatures());
  if (!popup) return 'unavailable';

  return new Promise<ReauthResult>((resolve) => {
    let settled = false;

    const finish = (result: ReauthResult) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      window.clearInterval(closeTimer);
      window.clearTimeout(timeoutTimer);
      try {
        popup.close();
      } catch {
        // Already gone. Nothing to do.
      }
      resolve(result);
    };

    function onMessage(event: MessageEvent) {
      // Three independent checks. The message can only legitimately come from a
      // page we opened, on our own origin, answering this specific request.
      if (event.origin !== window.location.origin) return;
      if (event.source !== popup) return;

      const data = event.data as Partial<HandoffMessage> | null;
      if (!data || data.type !== HANDOFF_MESSAGE_TYPE || data.nonce !== nonce) return;

      finish(data.status === 'ok' ? 'ok' : 'needs-login');
    }

    window.addEventListener('message', onMessage);

    // A popup that closes without reporting means the control plane had no
    // session and bowed out. Deliberately not a cross-origin message.
    const closeTimer = window.setInterval(() => {
      if (popup.closed) finish('needs-login');
    }, CLOSE_POLL_MS);

    const timeoutTimer = window.setTimeout(() => finish('unavailable'), TIMEOUT_MS);
  });
}
